import { createServerFn } from '@tanstack/react-start';
import chunk from 'lodash/chunk';
import { v7 } from 'uuid';
import z from 'zod';

import { yieldToEventLoop } from '../utils';
import { MAX_OBJECT_IDS_PER_REQUEST } from './utils';
import { getRuleKeys, remediateIssues } from './utils.function';

import { sourceCodeV3QueryOptions } from '@/api/endpoints/remedy';
import type { TLAbapFormatRequest, TLAbapFormatResponse } from '@/api/gen';
import type { TLSAPVersion } from '@/api/gen/tessera-backend-api.schemas';
import { sessionMiddleware } from '@/middleware/session';
import { getQueryClient } from '@/query';
import type {
  TLAINextFixObjectsWithIssuesItem,
  TLAINextFixObjectsWithIssuesRequest,
  TLAINextFixObjectWithIssuesRequest,
  TLAINextFixObjectWithIssuesResponse,
  TLAIRemedyV3ObjectDetail,
  TLAIUpdateSourceCodeRequest,
  TLAIUpdateSourceCodeResponse,
} from '@/types/api';
import { apiClient } from '@/utils/api-client';
import { isPresent, normalizeLineEndings, withRetry } from '@/utils/helpers';
import type { HTTPClient } from '@/utils/http-client';
import { extractHTTPError } from '@/utils/http-client';
import { safeInvoke } from '@/utils/safe-invoke';
import type { SessionData } from '@/utils/session';

type RemediateObjectUpdate = {
  object: TLAIRemedyV3ObjectDetail;
  code: string;
};

async function* writeSourceUpdates(
  httpClient: HTTPClient,
  remedy_id: string,
  updates: RemediateObjectUpdate[],
  formatOnSave: boolean,
  sapVersion?: string,
): AsyncGenerator<TLAINextFixObjectsWithIssuesItem, void, undefined> {
  for (const { object, code } of updates) {
    let codeToSave = code;

    if (formatOnSave && sapVersion) {
      const { data: formatResponse } = await safeInvoke(() =>
        httpClient.post<TLAbapFormatResponse, TLAbapFormatRequest>(
          {
            path: '/abap/format',
          },
          { code, sap_version: sapVersion as unknown as TLSAPVersion },
        ),
      );

      if (formatResponse?.formatted_code?.trim()?.length) {
        codeToSave = formatResponse.formatted_code;
      }
    }

    const { data: updateSourceCodeResponse, error: updateSourceCodeError } = await safeInvoke(() =>
      httpClient.post<TLAIUpdateSourceCodeResponse, TLAIUpdateSourceCodeRequest>(
        {
          path: '/code-remediation-v3/remedies',
          params: [remedy_id, 'update-source-code'],
        },
        { remedy_id, object_id: object.object_id, code: codeToSave },
      ),
    );

    if (updateSourceCodeError) {
      yield {
        object_id: object.object_id,
        success: false,
        error: extractHTTPError(updateSourceCodeError, 'An unknown error occurred while updating source code'),
      };
      continue;
    }

    if (!updateSourceCodeResponse?.success) {
      yield {
        object_id: object.object_id,
        success: false,
        error: updateSourceCodeResponse?.message ?? 'Unknown error',
      };
      continue;
    }

    yield { object_id: object.object_id, success: true };
  }
}

async function processFixForObject({
  remedy_id,
  object_id,
  issue_keys,
  abap_rule_keys,
  fle_rule_keys,
  skip_fle_remediation,
  session,
  skipCommentingRules,
}: {
  remedy_id: string;
  object_id: string;
  issue_keys: string[];
  abap_rule_keys: string[];
  fle_rule_keys: string[];
  skip_fle_remediation?: boolean;
  session: SessionData;
  skipCommentingRules: string[] | undefined;
}): Promise<{ item?: TLAINextFixObjectsWithIssuesItem; update?: RemediateObjectUpdate }> {
  const queryClient = getQueryClient();

  const { data, error } = await withRetry(
    () => queryClient.fetchQuery(sourceCodeV3QueryOptions({ remedy_id, object_id })),
    {
      retries: 2,
      delay: 500,
      shouldRetryOnSuccess: (result) => !result?.program?.code,
    },
  );

  if (error) {
    return {
      item: {
        object_id,
        success: false,
        error: extractHTTPError(error, 'An unknown error occurred while fetching source code'),
      },
    };
  }

  const normalizedCode = data.program.code ? normalizeLineEndings(data.program.code) : undefined;
  if (!normalizedCode) {
    return { item: { object_id, success: false, error: 'Source code not found' } };
  }

  const fixResponse = await remediateIssues({
    remedy_id,
    object_id: data.object.object_id,
    code: normalizedCode,
    issue_keys,
    abap_rule_keys,
    fle_rule_keys,
    skip_fle_remediation,
    session,
    skipCommentingRules,
  });

  if (fixResponse.error) {
    return { item: { object_id: data.object.object_id, success: false, error: fixResponse.error } };
  }

  if (!fixResponse.success) {
    return {
      item: { object_id: data.object.object_id, success: false, error: 'No fixes were applied to the code' },
    };
  }

  if (!fixResponse.hasAppliedFixes) {
    return { item: { object_id: data.object.object_id, success: true } };
  }

  return { update: { object: data.object, code: fixResponse.code } };
}

export const fixObjectsWithIssuesStream = createServerFn({ method: 'POST' })
  .middleware([sessionMiddleware])
  .inputValidator(
    z.object({
      session_id: z.string().optional(),
      remedy_id: z.string(),
      object_ids: z.array(z.string()),
      issue_keys: z.array(z.string()),
      abap_rule_keys: z.array(z.string()).optional(),
      fle_rule_keys: z.array(z.string()).optional(),
      skip_fle_remediation: z.boolean().optional().default(true),
      max_object_ids_per_request: z.number().optional().default(MAX_OBJECT_IDS_PER_REQUEST),
      skipCommentingRules: z.array(z.string()).optional(),
      format_on_save: z.boolean().optional().default(true),
      sap_version: z.string().optional(),
    }) satisfies z.ZodType<TLAINextFixObjectsWithIssuesRequest>,
  )
  .handler(async function* ({
    data: {
      session_id = v7(),
      remedy_id,
      object_ids,
      issue_keys,
      skip_fle_remediation,
      max_object_ids_per_request,
      skipCommentingRules,
      format_on_save = true,
      sap_version,
    },
    context: { session },
  }): AsyncGenerator<TLAINextFixObjectsWithIssuesItem[], void, undefined> {
    if (object_ids.length === 0 || issue_keys.length === 0) return;

    const { abapRuleKeys, fleRuleKeys } = await getRuleKeys(session, skip_fle_remediation);

    const apiClientInstance = await apiClient({ session });
    const objectIdBatches = chunk(object_ids, max_object_ids_per_request);

    console.info(
      `[FIX REMEDIATE][START] session_id=${session_id} remedy_id=${remedy_id} objects=${object_ids.length} issues=${issue_keys.length} batch_size=${max_object_ids_per_request} batches=${objectIdBatches.length}`,
    );

    for (let batchIndex = 0; batchIndex < objectIdBatches.length; batchIndex++) {
      const objectIdBatch = objectIdBatches[batchIndex];

      console.info(
        `[FIX REMEDIATE][BATCH] ${batchIndex + 1}/${objectIdBatches.length} batch_size=${objectIdBatch.length}`,
      );

      const plannedResults = await Promise.all(
        objectIdBatch.map((object_id) =>
          processFixForObject({
            remedy_id,
            object_id,
            issue_keys,
            abap_rule_keys: abapRuleKeys,
            fle_rule_keys: fleRuleKeys,
            skip_fle_remediation,
            session,
            skipCommentingRules,
          }),
        ),
      );

      yield plannedResults.map((r) => r.item).filter(isPresent);

      const updatesToWrite = plannedResults.map((r) => r.update).filter(isPresent);
      if (updatesToWrite.length === 0) continue;

      for await (const writeItems of writeSourceUpdates(
        apiClientInstance,
        remedy_id,
        updatesToWrite,
        format_on_save,
        sap_version,
      )) {
        yield [writeItems];
        await yieldToEventLoop();
      }
    }
  });

export const fixObjectWithIssues = createServerFn({ method: 'POST' })
  .middleware([sessionMiddleware])
  .inputValidator(
    z.object({
      remedy_id: z.string(),
      object_id: z.string(),
      code: z.string(),
      abap_rule_keys: z.array(z.string()).optional(),
      fle_rule_keys: z.array(z.string()).optional(),
      skip_fle_remediation: z.boolean().optional().default(true),
      issue_keys: z.array(z.string()),
      skipCommentingRules: z.array(z.string()).optional(),
    }) satisfies z.ZodType<TLAINextFixObjectWithIssuesRequest>,
  )
  .handler(
    async ({
      data: {
        remedy_id,
        object_id,
        code,
        abap_rule_keys = [],
        fle_rule_keys = [],
        skip_fle_remediation = true,
        issue_keys,
        skipCommentingRules,
      },
      context: { session },
    }): Promise<TLAINextFixObjectWithIssuesResponse> => {
      return remediateIssues({
        remedy_id,
        object_id,
        code,
        issue_keys,
        abap_rule_keys,
        fle_rule_keys,
        skip_fle_remediation,
        session,
        skipCommentingRules,
      });
    },
  );
